import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ServiceService } from '../../service.service'; // Add this import

interface Question {
  question_id: string;
  question_text: string;
  question_type: string;
  options?: string[];
  is_required: boolean;
  _id?: string;
}

interface QuestionnaireSet {
  set_id: string;
  set_name: string;
  questions: Question[];
  _id?: string;
}

interface SurveyData {
  survey_id: string;
  survey_title: string;
  survey_description: string;
  questionnaire_sets: QuestionnaireSet[];
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: any;
}

@Component({
  selector: 'app-question-edit',
  templateUrl: './question-edit.component.html',
  styleUrls: ['./question-edit.component.css']
})
export class QuestionEditComponent implements OnInit {
  surveyId: string = '';
  isLoading: boolean = false;
  isSaving: boolean = false;
  
  surveyData: SurveyData = {
    survey_id: '',
    survey_title: '',
    survey_description: '',
    questionnaire_sets: []
  };

  questionTypes = [
    { value: 'star_rating', label: 'Star Rating' },
    { value: 'multiple_choice', label: 'Multiple Choice' },
    { value: 'text', label: 'Text Answer' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private service: ServiceService // Add this
  ) { }

  ngOnInit(): void {
    // Get survey ID from route parameter
    this.route.params.subscribe(params => {
      this.surveyId = params['id'];
      if (this.surveyId) {
        this.loadSurveyData();
      }
    });
  }

  loadSurveyData(): void {
    this.isLoading = true;
    
    this.service.getCustomerSurveyDetail(this.surveyId).subscribe({
      next: (response: ApiResponse) => {
        if (response.success && response.data) {
          // Map the detail response to edit format
          this.surveyData = {
            survey_id: response.data.survey_id,
            survey_title: response.data.survey_title,
            survey_description: response.data.survey_description,
            questionnaire_sets: response.data.questionnaire_sets.map((set: any) => ({
              set_id: set.set_id,
              set_name: set.set_name,
              questions: set.questions.map((q: any) => ({
                question_id: q.question_id,
                question_text: q.question_text,
                question_type: q.question_type,
                options: q.options || [],
                is_required: q.is_required
              }))
            }))
          };
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading survey:', error);
        this.service.showToast('error', 'Failed to load survey data');
        this.isLoading = false;
      }
    });
  }

  // Add new question set
  addQuestionSet(): void {
    const newSetNumber = this.surveyData.questionnaire_sets.length + 1;
    const newSet: QuestionnaireSet = {
      set_id: `SET${String(newSetNumber).padStart(3, '0')}`,
      set_name: `Question Set ${newSetNumber}`,
      questions: []
    };
    this.surveyData.questionnaire_sets.push(newSet);
  }

  // Remove question set
  removeQuestionSet(index: number): void {
    if (confirm('Are you sure you want to remove this question set?')) {
      this.surveyData.questionnaire_sets.splice(index, 1);
    }
  }

  // Add new question to a set
  addQuestion(setIndex: number): void {
    const set = this.surveyData.questionnaire_sets[setIndex];
    const questionNumber = set.questions.length + 1;
    
    const newQuestion: Question = {
      question_id: `Q${String(questionNumber).padStart(3, '0')}`,
      question_text: '',
      question_type: 'star_rating',
      options: [],
      is_required: false
    };
    
    set.questions.push(newQuestion);
  }

  // Remove question from a set
  removeQuestion(setIndex: number, questionIndex: number): void {
    if (confirm('Are you sure you want to remove this question?')) {
      this.surveyData.questionnaire_sets[setIndex].questions.splice(questionIndex, 1);
    }
  }

  // Handle question type change
  onQuestionTypeChange(setIndex: number, questionIndex: number): void {
    const question = this.surveyData.questionnaire_sets[setIndex].questions[questionIndex];
    
    // Initialize options array for multiple choice
    if (question.question_type === 'multiple_choice' && (!question.options || question.options.length === 0)) {
      question.options = [''];
    } else if (question.question_type !== 'multiple_choice') {
      question.options = [];
    }
  }

  // Add option to multiple choice question
  addOption(setIndex: number, questionIndex: number): void {
    const question = this.surveyData.questionnaire_sets[setIndex].questions[questionIndex];
    if (!question.options) {
      question.options = [];
    }
    question.options.push('');
  }

  // Remove option from multiple choice question
  removeOption(setIndex: number, questionIndex: number, optionIndex: number): void {
    const question = this.surveyData.questionnaire_sets[setIndex].questions[questionIndex];
    if (question.options) {
      question.options.splice(optionIndex, 1);
    }
  }

  // Track option changes
  trackOption(setIndex: number, questionIndex: number, optionIndex: number, value: string): void {
    const question = this.surveyData.questionnaire_sets[setIndex].questions[questionIndex];
    if (question.options) {
      question.options[optionIndex] = value;
    }
  }

  // Validate form before submission
  validateForm(): boolean {
    if (!this.surveyData.survey_title.trim()) {
      this.service.showToast('error', 'Please enter a survey title');
      return false;
    }

    if (!this.surveyData.survey_description.trim()) {
      this.service.showToast('error', 'Please enter a survey description');
      return false;
    }

    if (this.surveyData.questionnaire_sets.length === 0) {
      this.service.showToast('error', 'Please add at least one question set');
      return false;
    }

    for (let i = 0; i < this.surveyData.questionnaire_sets.length; i++) {
      const set = this.surveyData.questionnaire_sets[i];
      
      if (!set.set_name.trim()) {
        this.service.showToast('error', `Please enter a name for question set ${i + 1}`);
        return false;
      }

      if (set.questions.length === 0) {
        this.service.showToast('error', `Question set "${set.set_name}" must have at least one question`);
        return false;
      }

      for (let j = 0; j < set.questions.length; j++) {
        const question = set.questions[j];
        
        if (!question.question_text.trim()) {
          this.service.showToast('error', `Please enter text for question ${j + 1} in set "${set.set_name}"`);
          return false;
        }

        if (question.question_type === 'multiple_choice') {
          if (!question.options || question.options.length === 0) {
            this.service.showToast('error', `Multiple choice question "${question.question_text}" must have at least one option`);
            return false;
          }

          const hasEmptyOption = question.options.some(opt => !opt.trim());
          if (hasEmptyOption) {
            this.service.showToast('error', `All options for question "${question.question_text}" must be filled`);
            return false;
          }
        }
      }
    }

    return true;
  }

  // Save/Update survey
  onSubmit(): void {
    if (!this.validateForm()) {
      return;
    }

    this.isSaving = true;

    // Prepare the data in the exact format expected by the API
    const updateData = {
      survey_id: this.surveyData.survey_id,
      survey_title: this.surveyData.survey_title,
      survey_description: this.surveyData.survey_description,
      questionnaire_sets: this.surveyData.questionnaire_sets.map(set => ({
        set_id: set.set_id,
        set_name: set.set_name,
        questions: set.questions.map(q => {
          const questionData: any = {
            question_id: q.question_id,
            question_text: q.question_text,
            question_type: q.question_type,
            is_required: q.is_required
          };

          // Only include options for multiple_choice questions
          if (q.question_type === 'multiple_choice' && q.options) {
            questionData.options = q.options.filter(opt => opt.trim());
          }

          return questionData;
        })
      }))
    };

    console.log('Submitting data:', updateData);

    this.service.updateCustomerSurvey(updateData).subscribe({
      next: (response: ApiResponse) => {
        if (response.success) {
          this.service.showToast('success', 'Survey updated successfully!');
          this.router.navigate(['/admin/customer-survey-menu/detail', this.surveyId]);
        } else {
          this.service.showToast('error', 'Failed to update survey: ' + response.message);
        }
        this.isSaving = false;
      },
      error: (error: any) => {
        console.error('Error updating survey:', error);
        this.service.showToast('error', 'Failed to update survey. Please try again.');
        this.isSaving = false;
      }
    });
  }

  // Cancel and go back
  onCancel(): void {
    if (confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      this.router.navigate(['/admin/customer-survey-menu/detail', this.surveyId]);
    }
  }

  // Go back to listing
  goBack(): void {
    this.router.navigate(['/admin/customer-survey-menu/listing']);
  }
}